import { useRef, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { Cross } from '@/libraries/wpl/foundations/src/icons/basic/cross';

export interface SearchFieldProps {
	readonly value: string;
	readonly onChange: (next: string) => void;
	readonly onSubmit: () => void;
	readonly onClear: () => void;
}

/**
 * Text-entry control with an embedded submit (magnifying glass) button and a
 * clear (X) button that appears when the field is focused.
 */
export const SearchField = ({ value, onChange, onSubmit, onClear }: SearchFieldProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isFocused, setIsFocused] = useState(false);

	const placeholder = intl.formatMessage({
		id: 'SearchBar.searchPlaceholder',
		defaultMessage: 'Search',
	});

	const fireFocusAnalyticsEvent = () => {
		createAnalyticsEvent({
			origin: 'web',
			platform: 'web',
			product: 'marketing',
			source: 'none',
			userIdType: 'atlassianAccount',
			eventComponent: 'searchInput',
			event: 'clicked',
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'component',
			attributes: {
				url: location.href,
				page_path: location.pathname,
				eventComponent: 'searchInput',
			},
		}).fire();
	};

	const handleFocus = () => {
		setIsFocused(true);
		fireFocusAnalyticsEvent();
	};

	const handleBlur = () => {
		if (value === '') setIsFocused(false);
	};

	const submitAndFocusInput = () => {
		inputRef.current?.focus();
		onSubmit();
	};

	const clearAndFocusInput = () => {
		onClear();
		inputRef.current?.focus();
	};

	const focusedClass = isFocused ? 'search-active' : '';

	return (
		<form
			css={searchFieldStyle}
			onSubmit={(event) => {
				event.preventDefault();
				submitAndFocusInput();
			}}
		>
			<input
				ref={inputRef}
				className={focusedClass}
				type="text"
				aria-label={placeholder}
				placeholder={placeholder}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				onFocus={handleFocus}
				onBlur={handleBlur}
			/>
			<div className={`search-buttons-container ${focusedClass}`}>
				{isFocused && (
					<>
						<SearchClearButton onClick={clearAndFocusInput} />
						<div className="search-buttons-divider" />
					</>
				)}
				<SearchSubmitButton />
			</div>
		</form>
	);
};

// ---------------------------------------------------------------------------
// Internal sub-components (private to this file; used only by SearchField)
// ---------------------------------------------------------------------------

const SearchSubmitButton = () => {
	const intl = useIntl();
	const label = intl.formatMessage({
		id: 'SearchButton.label',
		defaultMessage: 'Search',
	});

	return (
		<button
			type="submit"
			css={submitButtonStyle}
			aria-label={label}
			data-testid="search-submit-button"
			data-event="clicked"
			data-event-component="button"
			data-event-container="searchButton"
		>
			<span css={submitIconWrapperStyle}>
				<SearchGlyph />
			</span>
		</button>
	);
};

/**
 * Matches the Figma "in-Page Search" glyph exactly (node 933:335490) —
 * Atlaskit's stock search icon is a filled glyph with a different weight
 * than this stroke-based magnifying glass.
 */
const SearchGlyph = () => (
	<svg
		width="17"
		height="19"
		viewBox="0 0 19.9609 21.1938"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		focusable="false"
	>
		<path
			d="M15.428 16.6609L18.5859 19.8188"
			stroke="currentColor"
			strokeWidth="2.75"
			strokeLinecap="round"
		/>
		<path
			d="M9.17763 17.2303C13.625 17.2303 17.2303 13.625 17.2303 9.17763C17.2303 4.73029 13.625 1.125 9.17763 1.125C4.73029 1.125 1.125 4.73029 1.125 9.17763C1.125 13.625 4.73029 17.2303 9.17763 17.2303Z"
			stroke="currentColor"
			strokeWidth="2.25"
		/>
	</svg>
);

interface SearchClearButtonProps {
	readonly onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const SearchClearButton = ({ onClick }: SearchClearButtonProps) => {
	const intl = useIntl();
	const label = intl.formatMessage({
		id: 'SearchCloseButton.label',
		defaultMessage: 'Clear search',
	});

	return (
		<button
			type="button"
			css={clearButtonStyle}
			onClick={onClick}
			aria-label={label}
			data-testid="search-close-button"
			data-event="clicked"
			data-event-component="button"
			data-event-container="searchCloseButton"
		>
			<Cross size="16" css={clearIconStyle} />
		</button>
	);
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const searchFieldStyle = css({
	boxSizing: 'border-box',
	height: token('space.500'),
	marginBlockEnd: 0,
	minWidth: '280px',
	maxWidth: '420px',
	width: '100%',
	background: wacToken('color.neutral.light100'),
	border: `1px solid ${wacToken('color.neutral.light400')}`,
	borderRadius: '40px',
	display: 'flex',
	flexWrap: 'nowrap',
	justifyContent: 'space-between',
	padding: '8px 24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		border: 'none',
		background: 'transparent',
		outline: 'none',
		width: '100%',
		flexBasis: '90%',
		color: wacToken('color.primary.black'),
		fontFamily: wacToken('font.charlie.text'),
		fontSize: '16px',
		fontWeight: 400,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'input::placeholder': {
		color: wacToken('color.neutral.light700'),
		opacity: 1,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'input.search-active': {
		flexBasis: '80%',
	},

	[media.below.xs]: {
		minWidth: 0,
		maxWidth: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'input.search-active::placeholder': {
			color: 'transparent',
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.search-buttons-container': {
		flexBasis: '10%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexWrap: 'nowrap',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.search-buttons-divider': {
			margin: `0 ${token('space.100')}`,
			flexBasis: '1%',
			height: token('space.300'),
			borderInlineStart: `${token('border.width')} solid ${wacToken('color.primary.black')}`,
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.search-buttons-container.search-active': {
		flexBasis: '20%',
	},
});

const submitButtonStyle = css({
	background: 'transparent',
	border: 0,
	cursor: 'pointer',
	padding: 0,
	display: 'flex',
	alignItems: 'center',
	color: wacToken('color.neutral.dark100'),
});

const submitIconWrapperStyle = css({
	color: wacToken('color.primary.black'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '17px',
	height: '19px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: {
		width: '17px',
		height: '19px',
	},
});

const clearButtonStyle = css({
	background: 'transparent',
	border: 0,
	cursor: 'pointer',
	padding: 0,
	display: 'flex',
	alignItems: 'center',
	color: wacToken('color.neutral.dark100'),
	'&:hover': {
		color: wacToken('color.neutral.dark100'),
	},
});

const clearIconStyle = css({
	color: wacToken('color.primary.black'),
	borderRadius: '50%',
	height: 'auto',
});
